<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert, useTrack } from 'dashboard/composables';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportsAPI from 'dashboard/api/reports';
import { formatReportDuration, durationMetricTooltip } from './constants';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import ChatsWeekdayHeatmap from './components/ChatsWeekdayHeatmap.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useSummaryReportArchive } from './composables/useSummaryReportArchive';
import { buildBucketArchiveFilters } from './helpers/summaryReportArchiveHelper';
import {
  resolveDynamicsGranularity,
  formatPeriodRangeLabel,
} from './helpers/dynamicsGranularityHelper';

const { t } = useI18n();
const { openArchive, resolveMetricKeyFromCardKey } = useSummaryReportArchive();

const from = ref(0);
const to = ref(0);
const comparisonPeriods = ref([]);
const entityFilters = ref({});
const botOutcome = ref('all');
const summaryGroupBy = ref('source');
const loading = ref(false);
const report = ref({
  overview: {},
  funnel: {},
  dynamics: [],
  handoff_heatmap: [],
  handoff_buckets: [],
  summary: [],
  comparisons: [],
});

const outcomeOptions = computed(() => [
  { value: 'all', label: t('BOT_REPORTS.FILTERS.OUTCOME_ALL') },
  {
    value: 'resolved_by_bot',
    label: t('BOT_REPORTS.FILTERS.OUTCOME_RESOLVED'),
  },
  { value: 'handoff', label: t('BOT_REPORTS.FILTERS.OUTCOME_HANDOFF') },
  { value: 'fallback', label: t('BOT_REPORTS.FILTERS.OUTCOME_FALLBACK') },
]);

const requestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  ...entityFilters.value,
  botOutcome: botOutcome.value,
  summaryGroupBy: summaryGroupBy.value,
  comparisonPeriods: comparisonPeriods.value,
}));

const formatCount = value =>
  value || value === 0 ? Number(value).toLocaleString() : '0';

const formatRate = value =>
  value === null || value === undefined ? '—' : `${value}%`;

const formatWait = value =>
  value === null || value === undefined ? '—' : formatReportDuration(value);

const formatPeriodLabel = (since, until) => {
  const label = formatPeriodRangeLabel(since, until);
  return label ? `${label} UTC` : '';
};

const comparisonLabels = computed(() =>
  (comparisonPeriods.value || []).map(period =>
    formatPeriodLabel(period.from ?? period.since, period.to ?? period.until)
  )
);

const primaryPeriodLabel = computed(() =>
  formatPeriodLabel(from.value, to.value)
);

const hasData = computed(() => (report.value.overview?.bot_started || 0) > 0);

const overviewCards = computed(() => {
  const o = report.value.overview || {};
  const comparisons = report.value.comparisons || [];
  const formatComparisonValue = (key, overview) => {
    if (key === 'median_time_to_handoff') return formatWait(overview?.[key]);
    if (key.includes('rate')) return formatRate(overview?.[key]);
    return formatCount(overview?.[key]);
  };
  const buildComparisons = key =>
    comparisons.map((slice, index) => ({
      value: formatComparisonValue(key, slice.overview),
      periodLabel: comparisonLabels.value[index] || '',
    }));

  return [
    {
      key: 'started',
      label: t('BOT_REPORTS.CARDS.BOT_STARTED'),
      value: formatCount(o.bot_started),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.BOT_STARTED')
      ),
      comparisons: buildComparisons('bot_started'),
    },
    {
      key: 'resolved',
      label: t('BOT_REPORTS.CARDS.BOT_RESOLVED'),
      value: formatCount(o.bot_resolved),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.BOT_RESOLVED')
      ),
      comparisons: buildComparisons('bot_resolved'),
    },
    {
      key: 'resolution_rate',
      label: t('BOT_REPORTS.CARDS.BOT_RESOLUTION_RATE'),
      value: formatRate(o.bot_resolution_rate),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.BOT_RESOLUTION_RATE')
      ),
      comparisons: buildComparisons('bot_resolution_rate'),
    },
    {
      key: 'handed_off',
      label: t('BOT_REPORTS.CARDS.HANDED_OFF'),
      value: formatCount(o.handed_off),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.HANDED_OFF')
      ),
      comparisons: buildComparisons('handed_off'),
    },
    {
      key: 'handoff_rate',
      label: t('BOT_REPORTS.CARDS.HANDOFF_RATE'),
      value: formatRate(o.handoff_rate),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.HANDOFF_RATE')
      ),
      comparisons: buildComparisons('handoff_rate'),
    },
    {
      key: 'median_handoff',
      label: t('BOT_REPORTS.CARDS.MEDIAN_HANDOFF'),
      value: formatWait(o.median_time_to_handoff),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.MEDIAN_HANDOFF'),
        o.median_time_to_handoff
      ),
      comparisons: buildComparisons('median_time_to_handoff'),
    },
    {
      key: 'repeat',
      label: t('BOT_REPORTS.CARDS.REPEAT_REQUEST'),
      value: formatCount(o.repeat_operator_request),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.REPEAT_REQUEST')
      ),
      comparisons: buildComparisons('repeat_operator_request'),
    },
    {
      key: 'fallback',
      label: t('BOT_REPORTS.CARDS.FALLBACK'),
      value: formatCount(o.fallback),
      tooltip: durationMetricTooltip(
        t,
        t('BOT_REPORTS.CARDS.TOOLTIPS.FALLBACK')
      ),
      comparisons: buildComparisons('fallback'),
    },
  ];
});

const funnelCollection = computed(() => ({
  labels: [t('BOT_REPORTS.CHARTS.FUNNEL')],
  datasets: [
    {
      label: t('BOT_REPORTS.CHARTS.RESOLVED'),
      data: [report.value.funnel?.resolved_by_bot || 0],
      backgroundColor: '#10B981',
      stack: 'funnel',
    },
    {
      label: t('BOT_REPORTS.CHARTS.HANDED_OFF'),
      data: [report.value.funnel?.handed_off || 0],
      backgroundColor: '#3B82F6',
      stack: 'funnel',
    },
    {
      label: t('BOT_REPORTS.CHARTS.FALLBACK'),
      data: [report.value.funnel?.fallback || 0],
      backgroundColor: '#F97316',
      stack: 'funnel',
    },
    {
      label: t('BOT_REPORTS.CHARTS.OPEN'),
      data: [report.value.funnel?.open_at_period_end || 0],
      backgroundColor: '#64748B',
      stack: 'funnel',
    },
  ],
}));

const dynamicsCollection = computed(() => {
  const labels = (report.value.dynamics || []).map(row => row.date);
  const datasets = [
    {
      label: t('BOT_REPORTS.CHARTS.RESOLVED'),
      data: (report.value.dynamics || []).map(row => row.resolved_by_bot),
      borderColor: '#10B981',
      backgroundColor: '#10B981',
      tension: 0.3,
    },
    {
      label: t('BOT_REPORTS.CHARTS.HANDED_OFF'),
      data: (report.value.dynamics || []).map(row => row.handed_off),
      borderColor: '#3B82F6',
      backgroundColor: '#3B82F6',
      tension: 0.3,
    },
    {
      label: t('BOT_REPORTS.CHARTS.FALLBACK'),
      data: (report.value.dynamics || []).map(row => row.fallback),
      borderColor: '#F97316',
      backgroundColor: '#F97316',
      tension: 0.3,
    },
  ];

  (report.value.comparisons || []).forEach((slice, index) => {
    const byDate = Object.fromEntries(
      (slice.dynamics || []).map(row => [row.date, row])
    );
    const suffix = comparisonLabels.value[index]
      ? ` (${comparisonLabels.value[index]})`
      : ` (${index + 1})`;
    datasets.push(
      {
        label: `${t('BOT_REPORTS.CHARTS.RESOLVED')}${suffix}`,
        data: labels.map(date => byDate[date]?.resolved_by_bot || 0),
        borderColor: '#86EFAC',
        backgroundColor: '#86EFAC',
        borderDash: [4, 4],
        tension: 0.3,
      },
      {
        label: `${t('BOT_REPORTS.CHARTS.HANDED_OFF')}${suffix}`,
        data: labels.map(date => byDate[date]?.handed_off || 0),
        borderColor: '#93C5FD',
        backgroundColor: '#93C5FD',
        borderDash: [4, 4],
        tension: 0.3,
      },
      {
        label: `${t('BOT_REPORTS.CHARTS.FALLBACK')}${suffix}`,
        data: labels.map(date => byDate[date]?.fallback || 0),
        borderColor: '#FDBA74',
        backgroundColor: '#FDBA74',
        borderDash: [4, 4],
        tension: 0.3,
      }
    );
  });

  return { labels, datasets };
});

const handoffBucketsCollection = computed(() => ({
  labels: (report.value.handoff_buckets || []).map(row => row.label),
  datasets: [
    {
      label: t('BOT_REPORTS.CHARTS.HANDOFF_TIME'),
      data: (report.value.handoff_buckets || []).map(row => row.count),
      backgroundColor: '#6366F1',
    },
  ],
}));

const heatmapPoints = computed(() => report.value.handoff_heatmap || []);

const archiveContext = computed(() => ({
  from: from.value,
  to: to.value,
  reportType: 'bot',
  entityFilters: entityFilters.value,
}));

const dynamicsGranularity = computed(() =>
  resolveDynamicsGranularity(from.value, to.value)
);

const BOT_FUNNEL_METRICS = [
  'botResolvedCount',
  'botHandoffCount',
  'botFallbackCount',
  'unresolvedCount',
];

const BOT_DYNAMICS_METRICS = [
  'botResolvedCount',
  'botHandoffCount',
  'botFallbackCount',
];

const onCardClick = card => {
  const metricKey = resolveMetricKeyFromCardKey('bot', card.key);
  if (!metricKey) return;
  openArchive({
    metricKey,
    reportType: 'bot',
    row: null,
    context: archiveContext.value,
  });
};

const onFunnelBarClick = event => {
  const metricKey = BOT_FUNNEL_METRICS[event.datasetIndex];
  if (!metricKey) return;
  openArchive({
    metricKey,
    reportType: 'bot',
    row: null,
    context: archiveContext.value,
  });
};

const onDynamicsPointClick = event => {
  // Primary series occupy the first three datasets; comparison series follow.
  const metricKey = BOT_DYNAMICS_METRICS[event.datasetIndex % 3];
  const dateLabel = (report.value.dynamics || [])[event.index]?.date;
  if (!metricKey || !dateLabel) return;

  const filters = buildBucketArchiveFilters({
    metricKey,
    reportType: 'bot',
    row: null,
    context: archiveContext.value,
    dateLabel,
    granularity: dynamicsGranularity.value,
  });

  openArchive({
    metricKey,
    reportType: 'bot',
    row: null,
    context: archiveContext.value,
    filters,
  });
};

const onHandoffBucketsClick = event => {
  const row = (report.value.handoff_buckets || [])[event.index];
  if (!row?.count) return;

  openArchive({
    metricKey: 'botHandoffCount',
    reportType: 'bot',
    row: null,
    context: archiveContext.value,
  });
};

const onSummaryRowClick = row => {
  const nextFilters = { ...entityFilters.value };
  if (row.group_type === 'source' && row.id) {
    nextFilters.inboxIds = [row.id];
  } else if (row.group_type === 'team' && row.id) {
    nextFilters.teamIds = [row.id];
  } else if (row.group_type === 'tag' && row.name && row.name !== '—') {
    nextFilters.labelTitles = [row.name];
  }
  openArchive({
    metricKey: 'botStartedCount',
    reportType: 'bot',
    row: null,
    context: {
      ...archiveContext.value,
      entityFilters: nextFilters,
    },
  });
};

const stackedOptions = {
  scales: {
    x: { stacked: true, grid: { drawOnChartArea: false } },
    y: { stacked: true, beginAtZero: true, grid: { drawOnChartArea: false } },
  },
};

const fetchBotPage = async () => {
  if (!from.value || !to.value) return;
  loading.value = true;
  try {
    const { data } = await ReportsAPI.getBotPage(requestPayload.value);
    report.value = {
      overview: {},
      funnel: {},
      dynamics: [],
      handoff_heatmap: [],
      handoff_buckets: [],
      summary: [],
      comparisons: [],
      ...data,
    };
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    loading.value = false;
  }
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  comparisonPeriods.value = date.comparisonPeriods || [];
  entityFilters.value = entity;
  if (!botOutcome.value) botOutcome.value = 'all';
  fetchBotPage();

  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterValue: {
      from: date.from,
      to: date.to,
    },
    reportType: 'bots',
  });
};

const downloadCsv = async () => {
  try {
    await ReportsAPI.downloadSectionCsv(
      'bot',
      requestPayload.value,
      generateStatisticsFileName({
        section: 'bot',
        from: from.value,
        to: to.value,
      })
    );
  } catch {
    // surfaced by downloadStatisticsCsvAsync
  }
};

const setSummaryGroupBy = value => {
  summaryGroupBy.value = value;
  fetchBotPage();
};

watch(botOutcome, () => {
  if (!botOutcome.value) botOutcome.value = 'all';
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <ReportHeader :header-title="$t('BOT_REPORTS.HEADER')">
      <V4Button
        :label="$t('BOT_REPORTS.DOWNLOAD')"
        icon="i-ph-file-arrow-down"
        size="sm"
        @click="downloadCsv"
      />
    </ReportHeader>

    <p class="text-sm text-n-slate-11">
      {{ $t('BOT_REPORTS.DESCRIPTION') }}
    </p>

    <ReportFiltersSection
      type="bot"
      :disabled="loading"
      hide-agent-filter
      hide-resolution-method
      @filters-apply="onFiltersApply"
    >
      <template #date="{ onDateChange }">
        <ReportFilters
          :show-entity-filter="false"
          :show-group-by="false"
          :show-business-hours="false"
          :sync-url="false"
          @filter-change="onDateChange"
        />
      </template>
      <div class="mt-2 min-w-[14rem] max-w-xs">
        <ComboBox
          v-model="botOutcome"
          :options="outcomeOptions"
          :placeholder="$t('BOT_REPORTS.FILTERS.BOT_OUTCOME')"
        />
      </div>
    </ReportFiltersSection>

    <p class="text-xs text-n-slate-11">
      {{ $t('BOT_REPORTS.COHORT_NOTE') }}
    </p>
    <p v-if="primaryPeriodLabel" class="text-xs text-n-slate-11">
      {{ $t('BOT_REPORTS.PERIOD_UTC', { period: primaryPeriodLabel }) }}
    </p>

    <div
      class="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8 rounded-xl bg-n-solid-2 px-4 py-4 outline outline-1 outline-n-container"
    >
      <button
        v-for="card in overviewCards"
        :key="card.key"
        type="button"
        class="flex flex-col gap-1 text-left cursor-pointer"
        @click="onCardClick(card)"
      >
        <span class="text-xs text-n-slate-11 inline-flex items-center gap-1">
          {{ card.label }}
          <span
            v-if="card.tooltip"
            v-tooltip.right="card.tooltip"
            class="i-lucide-info flex-shrink-0 text-n-slate-10 size-3"
          />
        </span>
        <span class="text-xl font-medium text-n-slate-12">{{
          card.value
        }}</span>
        <span
          v-for="(comp, idx) in card.comparisons"
          :key="`${card.key}-${idx}`"
          class="text-[11px] text-n-slate-10"
        >
          {{ comp.value }}
          <span v-if="comp.periodLabel">{{ ` · ${comp.periodLabel}` }}</span>
        </span>
      </button>
    </div>

    <div
      v-if="!loading && !hasData"
      class="rounded-xl bg-n-solid-2 px-5 py-10 text-center text-sm text-n-slate-11 outline outline-1 outline-n-container"
    >
      {{ $t('BOT_REPORTS.EMPTY') }}
    </div>

    <template v-else>
      <div class="grid gap-4 lg:grid-cols-2">
        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('BOT_REPORTS.CHARTS.FUNNEL') }}
          </h3>
          <div class="h-64">
            <BarChart
              :collection="funnelCollection"
              show-legend
              :chart-options="stackedOptions"
              @bar-click="onFunnelBarClick"
            />
          </div>
        </section>

        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('BOT_REPORTS.CHARTS.DYNAMICS') }}
          </h3>
          <div class="h-64">
            <LineChart
              :collection="dynamicsCollection"
              show-legend
              @point-click="onDynamicsPointClick"
            />
          </div>
        </section>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <ChatsWeekdayHeatmap
          :points="heatmapPoints"
          :title="$t('BOT_REPORTS.CHARTS.HANDOFF_HEATMAP')"
          :archive-context="archiveContext"
          metric-key="botHandoffCount"
        />

        <section
          class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
        >
          <h3 class="mb-3 text-base font-medium text-n-slate-12">
            {{ $t('BOT_REPORTS.CHARTS.HANDOFF_TIME') }}
          </h3>
          <div class="h-64">
            <BarChart
              :collection="handoffBucketsCollection"
              @bar-click="onHandoffBucketsClick"
            />
          </div>
        </section>
      </div>

      <section
        class="overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-5 py-3"
        >
          <h3 class="mb-0 text-base font-medium text-n-slate-12">
            {{ $t('BOT_REPORTS.SUMMARY.TITLE') }}
          </h3>
          <div class="flex gap-1">
            <button
              type="button"
              class="rounded-lg px-2.5 py-1 text-xs"
              :class="
                summaryGroupBy === 'source'
                  ? 'bg-n-brand text-white'
                  : 'border border-n-weak text-n-slate-11'
              "
              @click="setSummaryGroupBy('source')"
            >
              {{ $t('BOT_REPORTS.SUMMARY.SOURCE') }}
            </button>
            <button
              type="button"
              class="rounded-lg px-2.5 py-1 text-xs"
              :class="
                summaryGroupBy === 'tag'
                  ? 'bg-n-brand text-white'
                  : 'border border-n-weak text-n-slate-11'
              "
              @click="setSummaryGroupBy('tag')"
            >
              {{ $t('BOT_REPORTS.SUMMARY.TAG') }}
            </button>
            <button
              type="button"
              class="rounded-lg px-2.5 py-1 text-xs"
              :class="
                summaryGroupBy === 'team'
                  ? 'bg-n-brand text-white'
                  : 'border border-n-weak text-n-slate-11'
              "
              @click="setSummaryGroupBy('team')"
            >
              {{ $t('BOT_REPORTS.SUMMARY.TEAM') }}
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-xs">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="px-3 py-2 text-left font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.ENTITY') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.STARTED') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.RESOLVED') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.RESOLUTION_RATE') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.HANDED_OFF') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.MEDIAN_HANDOFF') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.REPEAT') }}
                </th>
                <th class="px-3 py-2 text-center font-medium">
                  {{ $t('BOT_REPORTS.SUMMARY.FALLBACK') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.summary"
                :key="`${row.group_type}-${row.id}`"
                class="border-b border-n-weak/60 cursor-pointer hover:bg-n-alpha-1"
                @click="onSummaryRowClick(row)"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ row.name }}</td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.bot_started }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.bot_resolved }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ formatRate(row.bot_resolution_rate) }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.handed_off }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ formatWait(row.median_time_to_handoff) }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.repeat_operator_request }}
                </td>
                <td class="px-3 py-2 text-center tabular-nums">
                  {{ row.fallback }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <div
      v-if="loading"
      class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
    >
      <Spinner :size="32" class="text-n-brand" />
    </div>
  </div>
</template>
